/**
 * Browser Web Push configuration for customer order-status notifications.
 *
 * The VAPID public key is a build-time value (`REACT_APP_VAPID_PUBLIC_KEY`), following the
 * existing `REACT_APP_*` convention in `@/config/api`. It is public by design — it is sent to
 * the push service as `applicationServerKey`. The matching PRIVATE key is a server secret and
 * must never appear in this bundle or in any browser-visible configuration.
 *
 * Read lazily (never captured in a module-level constant) so a host environment change does not
 * require a code change, and so tests can vary it.
 */

/** Single service worker for this origin. Copied verbatim from `public/` into the build root. */
export const PUSH_SERVICE_WORKER_PATH = "/rasoix-push-sw.js";

/** Registration scope — the storefront is served from the origin root under every entry mode. */
export const PUSH_SERVICE_WORKER_SCOPE = "/";

export function getVapidPublicKey(): string {
  return (process.env.REACT_APP_VAPID_PUBLIC_KEY ?? "").trim();
}

/**
 * True only when this browser exposes every API the opt-in flow needs.
 *
 * Deliberately checks each capability separately rather than sniffing the user agent: iOS
 * Safari, for example, exposes `serviceWorker` in a normal tab but not `window.Notification`
 * unless the site was installed to the Home Screen, and this returns false there.
 */
export function isWebPushSupported(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  if (!("serviceWorker" in navigator)) return false;
  if (!("PushManager" in window)) return false;
  if (typeof window.Notification === "undefined") return false;
  if (typeof window.Notification.requestPermission !== "function") return false;
  return true;
}

/** Current permission without prompting. `"default"` when notifications are unavailable. */
export function getNotificationPermission(): NotificationPermission {
  if (!isWebPushSupported()) return "default";
  try {
    return window.Notification.permission;
  } catch {
    return "default";
  }
}

/**
 * Base64url VAPID key → the `Uint8Array` `PushManager.subscribe` requires.
 * Returns null for a missing or malformed key so the caller can fail closed.
 */
export function urlBase64ToUint8Array(base64String: string): Uint8Array | null {
  const raw = base64String.trim();
  if (!raw) return null;

  const padding = "=".repeat((4 - (raw.length % 4)) % 4);
  const base64 = (raw + padding).replace(/-/g, "+").replace(/_/g, "/");

  try {
    const binary = window.atob(base64);
    const output = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      output[i] = binary.charCodeAt(i);
    }
    return output;
  } catch {
    return null;
  }
}
